import { FC, useContext, useState, useMemo, useEffect } from "react";
import { useRouter } from "next/router";
import { AuthProvider } from "pages";
import { MenuItemDef } from "types/app";
import { MENU_DEFINITIONS, RESOURCE_DEFINITION } from "constant";
import {
  List,
  ListItem,
  Divider,
  ListItemIcon,
  Collapse,
  ListItemText,
  Tooltip,
  TextField,
  Zoom,
  IconButton,
  ThemeProvider,
  StyledEngineProvider,
  styled,
} from "@mui/material";
import { Close, ExpandLessSharp, ExpandMoreSharp } from "@mui/icons-material";
import AppLayoutContext from "components/context/AppLayoutContext";
import dynamic from "next/dynamic";
import { useDebounce } from "ahooks/lib";
import cloneDeep from "lodash/cloneDeep";
import { lightTheme } from "theme";

const onClick = (menu: string) => () => {
  const _paq = (window as any)._paq || [];
  _paq.push(["setCustomVariable", 2, "Menu", menu, "page"]);
  _paq.push(["trackEvent", "Menu", "track", "Usage", menu]);
};

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  "& .MuiListItemText-primary": {
    color: "#666666",
    fontSize: 15,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "&.Mui-selected .MuiListItemText-primary": {
    color: "#121212",
  },
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  marginLeft: 4,
  borderLeft: "4px solid white",
  "&.Mui-selected": {
    backgroundColor: "#F7F7F7 !important",
    borderColor: "#292929 !important",
  },
}));

const StyledListItemIcon = styled(ListItemIcon)({
  minWidth: 36,
});

const isMenuSelected = (def: MenuItemDef) => {
  return window.location.pathname === def.href;
};

const AppMenu: FC = () => {
  const authDetail = useContext(AuthProvider);
  const { open, hoverOpen } = useContext(AppLayoutContext);

  const shouldMenuShow = (item: MenuItemDef) =>
    authDetail?.menus.includes(item.authKey ?? "") &&
    !item.summaryRoute &&
    !item.hide &&
    (!item.toggle || (item.toggle && authDetail?.toggles && authDetail?.toggles[item.toggle]));

  const [value, setValue] = useState<string>("");
  const debouncedValue = useDebounce(value, { wait: 500 });

  const renderMenu = (defs: MenuItemDef[], keyword: string) => (
    <List>
      {defs
        .filter(function f(o): boolean {
          if (!keyword) return true;
          return (
            o.title.toLowerCase().includes(keyword.toLowerCase()) ||
            (!!o.children && (o.children = o.children.filter(f)).length > 0)
          );
        })
        .reduce(function cb(result, cur) {
          const res = (cur.children || []).reduce(cb, []);
          if (res.length) {
            cur.children = res;
            // push parent menu to the list
            result.push(cur);
          }
          if (shouldMenuShow(cur)) {
            result.push(cur);
          }
          return result;
        }, [] as MenuItemDef[])
        .map((def) => (
          <MenuList
            def={def}
            key={def.title}
            shouldOpen={!!keyword}
          />
        ))}
    </List>
  );

  const menus = useMemo(
    () => renderMenu(cloneDeep(MENU_DEFINITIONS), debouncedValue),
    [location.pathname, debouncedValue]
  );

  const resources = useMemo(
    () =>
      renderMenu(
        RESOURCE_DEFINITION.map((def) => ({
          ...def,
          icon: def.children ? "/menu/group.svg" : "/menu/link.svg",
          children: def.children
            ? def.children.map((x) => ({ ...x, icon: "/menu/link.svg" }))
            : undefined,
        })),
        debouncedValue
      ),
    [location.pathname, debouncedValue]
  );

  const resourcesButton = useMemo(
    () => (
      <List>
        <Tooltip
          title="Resources"
          enterDelay={1000}
          enterNextDelay={1000}
        >
          <StyledListItem>
            <StyledListItemIcon>
              <img
                src="/menu/link.svg"
                width={16}
                height={16}
              />
            </StyledListItemIcon>
            <StyledListItemText primary="Resources" />
          </StyledListItem>
        </Tooltip>
      </List>
    ),
    []
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={lightTheme}>
        <nav style={{ overflowX: "hidden" }}>
          <div style={{ padding: "0 20px" }}>
            <TextField
              size="medium"
              fullWidth
              variant="standard"
              value={value}
              InputProps={{
                sx: {
                  "&::before": {
                    bottom: -4,
                    display: open || hoverOpen ? "block" : "none",
                  },
                  "&::after": {
                    bottom: -4,
                    display: open || hoverOpen ? "block" : "none",
                  },
                },
                startAdornment: (
                  <img
                    style={{
                      marginLeft: 4,
                      objectFit: "cover",
                      maxWidth: 18,
                      height: 18,
                    }}
                    src="/menu/search.svg"
                  />
                ),
                endAdornment: (
                  <Zoom in={!!debouncedValue}>
                    <IconButton
                      size="small"
                      onClick={() => setValue("")}
                    >
                      <Close
                        style={{
                          filter: "opacity(0.8)",
                          fontSize: "1.1rem",
                        }}
                      />
                    </IconButton>
                  </Zoom>
                ),
              }}
              inputProps={{
                style: {
                  fontSize: 16,
                  lineHeight: "18px",
                  paddingLeft: 16,
                },
              }}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          {menus}
          <Divider sx={{ my: 1.5 }} />
          {open || hoverOpen ? (
            <p
              style={{
                color: "#666666",
                fontSize: 14,
                lineHeight: "13px",
                fontWeight: 600,
                marginLeft: 24,
                marginTop: 24,
              }}
            >
              RESOURCES
            </p>
          ) : null}
          {open || hoverOpen ? resources : resourcesButton}
        </nav>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const MenuList: FC<{ def: MenuItemDef; shouldOpen?: boolean }> = ({ def, shouldOpen }) => {
  const router = useRouter();

  return def.children ? (
    <ExpandableList
      def={def}
      shouldOpen={shouldOpen}
    />
  ) : (
    <Tooltip
      title={def.title}
      enterDelay={1000}
      enterNextDelay={1000}
    >
      <StyledListItem
        alignItems="center"
        //@ts-ignore
        button={true}
        onClick={(e) => {
          onClick(def.title)();
          if (def.external) {
            window.open(def.href as string, "_blank");
          } else {
            router.push(def.href as string);
          }
        }}
        selected={isMenuSelected(def)}
      >
        {def.icon && (
          <StyledListItemIcon>
            <img
              width={16}
              height={16}
              src={def.icon}
            />
          </StyledListItemIcon>
        )}
        <StyledListItemText primary={def.title} />
      </StyledListItem>
    </Tooltip>
  );
};

const ExpandableListItem: FC<{ item: MenuItemDef }> = ({ item }) => {
  const router = useRouter();

  return (
    <Tooltip
      title={item.title}
      enterDelay={1000}
      enterNextDelay={1000}
    >
      <StyledListItem
        //@ts-ignore
        button={true}
        onClick={(e) => {
          onClick(item.title)();
          if (item.external) {
            window.open(item.href as string, "_blank");
          } else {
            router.push(item.href as string);
          }
        }}
        sx={{ pl: 6.5 }}
        selected={isMenuSelected(item)}
      >
        <StyledListItemText primary={item.title} />
      </StyledListItem>
    </Tooltip>
  );
};

const ExpandableList: FC<{ def: MenuItemDef; shouldOpen?: boolean }> = ({ def, shouldOpen }) => {
  const [open, setOpen] = useState(false);
  const { open: drawerOpen, hoverOpen } = useContext(AppLayoutContext);

  useEffect(() => {
    if (!drawerOpen && !hoverOpen && open) {
      setOpen(false);
    }
    if ((drawerOpen || hoverOpen) && def.children?.filter((x) => isMenuSelected(x))?.length) {
      setOpen(true);
    }
  }, [drawerOpen, hoverOpen]);

  useEffect(() => {
    setOpen(!!shouldOpen);
  }, [shouldOpen]);

  return (
    <>
      <Tooltip
        title={def.title}
        enterDelay={1000}
        enterNextDelay={1000}
      >
        <StyledListItem
          alignItems="center"
          onClick={() => setOpen((prv) => !prv)}
          selected={def.children!.some((y) => isMenuSelected(y))}
        >
          {def.icon && (
            <StyledListItemIcon>
              <img
                width={16}
                height={16}
                src={def.icon}
              />
            </StyledListItemIcon>
          )}
          <StyledListItemText primary={def.title} />
          {open ? (
            <ExpandLessSharp sx={{ color: "#898989" }} />
          ) : (
            <ExpandMoreSharp sx={{ color: "#898989" }} />
          )}
        </StyledListItem>
      </Tooltip>
      <Collapse in={open}>
        <List disablePadding>
          {def.children!.map((x) => (
            <ExpandableListItem
              key={x.title}
              item={x}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};

export default dynamic(() => Promise.resolve(AppMenu), { ssr: false });
